import { Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';

import { styles } from './RetailInvoicePDF.css';
import { RetailInvoicePDFProps } from './RetailInvoicePDF.types';
import { formatAmountShort } from './RetailInvoicePDF.utils';

// ────────────────────────────────────────────────────────────
// Event date formatter: "M/D, h:mmA" e.g. "7/25, 7:30PM"
// ────────────────────────────────────────────────────────────
const formatEventDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'M/d, h:mmaaa')
      .replace('am', 'AM')
      .replace('pm', 'PM');
  } catch {
    return dateStr;
  }
};

export const EventDetailsTable = ({
  labels,
  orderDetailRows,
}: Pick<RetailInvoicePDFProps, 'orderDetailRows' | 'labels'>) => {
  return (
    <View style={[styles.tableContainer, styles.tableContainerWithMarginBottom]}>
      {/* Header */}
      <View style={styles.tableHeaderRow}>
        <View
          style={[styles.tableHeaderCellWrapper, styles.eventColHeaderEvent]}
        >
          <Text style={styles.tableHeaderCell}>{labels?.event ?? 'Event'}</Text>
        </View>
        <View
          style={[styles.tableHeaderCellWrapper, styles.eventColHeaderSection]}
        >
          <Text style={styles.tableHeaderCell}>
            {labels?.section ?? 'Section'}
          </Text>
        </View>
        <View
          style={[styles.tableHeaderCellWrapper, styles.eventColHeader14]}
        >
          <Text style={styles.tableHeaderCell}>{labels?.row ?? 'Row'}</Text>
        </View>
        <View
          style={[styles.tableHeaderCellWrapper, styles.eventColHeader14]}
        >
          <Text style={styles.tableHeaderCell}>
            {labels?.seatRange ?? 'Seat Range'}
          </Text>
        </View>
        <View
          style={[styles.tableHeaderCellWrapper, styles.eventColHeaderQty]}
        >
          <Text style={styles.tableHeaderCell}>{labels?.qty ?? 'Qty'}</Text>
        </View>
        <View
          style={[styles.tableHeaderCellWrapper, styles.eventColHeader14]}
        >
          <Text style={styles.tableHeaderCell}>
            {labels?.proceedHeader ?? 'Proceed ($)'}
          </Text>
        </View>
      </View>
      {/* Data rows — one per event; no row-level padding; each cell uses
                paddingLeft like the header */}
      {orderDetailRows.map((row, index) => (
        <View
          key={index}
          style={[styles.tableRow, styles.eventTableDataRow]}
        >
          {/* Event cell: name + date @ venue */}
          <View style={styles.eventCellEvent}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-expect-error numberOfLines is a valid runtime prop but missing from @react-pdf/renderer types */}
            <Text style={styles.eventNameText} numberOfLines={1}>
              {row.eventName ?? '—'}
            </Text>
            {(row.eventDate || row.venueName) && (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error numberOfLines is a valid runtime prop but missing from @react-pdf/renderer types
              <Text style={styles.eventSubText} numberOfLines={1}>
                {[
                  row.eventDate ? formatEventDate(row.eventDate) : '',
                  row.venueName ? `@ ${row.venueName}` : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
            )}
          </View>
          <View style={styles.eventCellSection}>
            <Text style={styles.eventCellText}>
              {row.section ?? '—'}
            </Text>
          </View>
          <View style={styles.eventCellRow}>
            <Text style={styles.eventCellText}>
              {row.row ?? '—'}
            </Text>
          </View>
          <Text style={styles.eventCellSeatRange}>
            {row.seats ??
              (row.seatFrom && row.seatTo
                ? `${row.seatFrom}–${row.seatTo}`
                : (row.seatFrom ?? '—'))}
          </Text>
          <Text style={styles.eventCellQty}>
            {row.qty ?? '—'}
          </Text>
          <Text style={styles.eventCellProceed}>
            {formatAmountShort(row.proceed ?? 0)}
          </Text>
        </View>
      ))}
    </View>
  );
};
